import React from 'react';
import { Phone, Mail, MapPin, Sparkles } from 'lucide-react';
import { contactInfo, belgianCities } from '../mockData';

const Footer = () => {
  const featuredCities = belgianCities.slice(0, 20);
  const allCitiesForSEO = belgianCities;
  
  return (
    <footer className="relative bg-black border-t border-yellow-500/20 py-16 px-4 overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-600/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-600 via-yellow-500 to-amber-600 flex items-center justify-center shadow-lg">
                <Sparkles className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-white text-2xl font-bold display-font">Marabout Belgique</h3>
                <p className="text-yellow-500 text-xs serif-font">Voyance & Spiritualité</p>
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed text-lg">
              Expert en magie blanche, magie rouge et retour affectif. Plus de 20 ans d'expérience au service de votre bonheur.
            </p>
            <div className="space-y-3">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center space-x-3 text-gray-400 hover:text-yellow-500 transition-colors group">
                <Phone size={20} className="group-hover:scale-110 transition-transform" />
                <span className="text-lg font-semibold">{contactInfo.phone}</span>
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="text-white text-2xl font-bold mb-6 display-font">Nos Services</h3>
            <ul className="text-gray-400 space-y-3 text-lg">
              <li className="hover:text-yellow-500 transition-colors cursor-pointer">• Retour de l'être aimé</li>
              <li className="hover:text-yellow-500 transition-colors cursor-pointer">• Envoûtement d'amour</li>
              <li className="hover:text-yellow-500 transition-colors cursor-pointer">• Magie blanche</li>
              <li className="hover:text-yellow-500 transition-colors cursor-pointer">• Magie rouge</li>
              <li className="hover:text-yellow-500 transition-colors cursor-pointer">• Protection contre le mauvais œil</li>
              <li className="hover:text-yellow-500 transition-colors cursor-pointer">• Chance aux jeux</li>
              <li className="hover:text-yellow-500 transition-colors cursor-pointer">• Réussite professionnelle</li>
              <li className="hover:text-yellow-500 transition-colors cursor-pointer">• Voyance et guidance</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-white text-2xl font-bold mb-6 display-font flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_voyant-belgium/artifacts/umxywgo0_IMG_1280.jpeg"
                alt="Localisation"
                className="w-6 h-6 mr-2"
              />
              Villes principales
            </h3>
            <div className="grid grid-cols-2 gap-3 text-gray-400">
              {featuredCities.map((city, index) => (
                <div key={index} className="flex items-center space-x-2 hover:text-yellow-500 transition-colors cursor-pointer">
                  <MapPin size={14} />
                  <span>{city}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Hidden SEO Links Section */}
        <div className="border-t border-white/10 pt-8 mb-8">
          <details className="text-gray-500 text-xs">
            <summary className="cursor-pointer hover:text-yellow-500 mb-4">Toutes nos localisations en Belgique</summary>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4">
              {allCitiesForSEO.map((city, index) => (
                <a 
                  key={index} 
                  href={`#${city.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-gray-600 hover:text-yellow-500 transition-colors text-xs"
                >
                  Marabout {city}
                </a>
              ))}
            </div>
          </details>
        </div>
        
        {/* SEO Keywords Links */}
        <div className="border-t border-white/10 pt-8 mb-8">
          <div className="text-gray-600 text-xs leading-relaxed">
            <span className="text-gray-500">Services:</span> 
            <a href="#marabout-voyant" className="hover:text-yellow-500 mx-1">Marabout Voyant</a> |
            <a href="#voyant-marabout" className="hover:text-yellow-500 mx-1">Voyant Marabout</a> |
            <a href="#marabout-medium" className="hover:text-yellow-500 mx-1">Marabout Medium</a> |
            <a href="#medium-marabout" className="hover:text-yellow-500 mx-1">Medium Marabout</a> |
            <a href="#voyant-medium" className="hover:text-yellow-500 mx-1">Voyant Medium</a> |
            <a href="#marabout-serieux" className="hover:text-yellow-500 mx-1">Marabout Sérieux</a> |
            <a href="#voyant-serieux" className="hover:text-yellow-500 mx-1">Voyant Sérieux</a> |
            <a href="#medium-serieux" className="hover:text-yellow-500 mx-1">Medium Sérieux</a> |
            <a href="#marabout-efficace" className="hover:text-yellow-500 mx-1">Marabout Efficace</a> |
            <a href="#marabout-puissant" className="hover:text-yellow-500 mx-1">Marabout Puissant</a> |
            <a href="#retour-affectif" className="hover:text-yellow-500 mx-1">Retour Affectif</a> |
            <a href="#envoutement-amour" className="hover:text-yellow-500 mx-1">Envoûtement Amour</a> |
            <a href="#magie-blanche" className="hover:text-yellow-500 mx-1">Magie Blanche</a> |
            <a href="#magie-rouge" className="hover:text-yellow-500 mx-1">Magie Rouge</a> |
            <a href="#faire-revenir-ex" className="hover:text-yellow-500 mx-1">Faire Revenir Son Ex</a> |
            <a href="#recuperer-ex" className="hover:text-yellow-500 mx-1">Récupérer Son Ex</a>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8">
          <div className="text-center text-gray-400">
            <p className="mb-3 text-lg">© 2025 <span className="text-yellow-500 font-semibold">Marabout Belgique</span>. Tous droits réservés.</p>
            <p className="text-sm leading-relaxed max-w-4xl mx-auto">
              Marabout voyant medium professionnel en Belgique - Services disponibles dans toutes les villes : <span className="text-gray-300">{belgianCities.slice(0, 15).join(', ')}</span> et plus...
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;